/**
 * Product cell widgets (#1805 slice A4a).
 *
 * Three columns land here, and each needed a different landing spot on `Widget`:
 *
 *   `title`         → `render` branched on `RendererContext.mode`. The v2 axis
 *                     is `WidgetProps.surface`, set as `surface: mode` by
 *                     `displayProps` in
 *                     `packages/resource-kit-ui/react/registry/field-type-registry.tsx::displayProps`,
 *                     `'detail'` originating in the `detailColumns` memo in
 *                     `packages/resource-kit-ui/react/resource/ResourcePage.tsx`.
 *   `options`       → `transform`, which `ModelField` has no slot for at all.
 *                     The legacy carrier ran ahead
 *                     of BOTH the cell and the export in the retired
 *                     `schema-utils`, so the widget has to transform in both
 *                     projections. It borrows the `json` type's own display and
 *                     text so the derived object still counts its keys in a row
 *                     and tabulates them in the drawer.
 *   `shipping_date` → `render` PLUS `filterType: 'date'` on a `type: 'text'`
 *                     column. `ModelField` has no `filterType`; the landing spot
 *                     is `Widget.filterType`, consulted by `toFilterType` at
 *                     `packages/resource-kit-controllers/widgets/filter-type.ts::toFilterType`.
 *                     Declared below, or the
 *                     QueryBuilder silently offers string operators over a date.
 *
 * `title` and `shipping_date` declare `csvText: rawCsv` — the text the
 * bespoke-renderer branch of `formatFieldTextForExport` already produced
 * (`packages/resource-kit-ui/react/registry/export-text.ts::formatFieldTextForExport`). `options` declares
 * the transformed `json` text
 * instead, because that is what a `transform` column always exported.
 *
 * NOTHING here may import a service — see `features/customers/widgets.tsx` for
 * the measured reason (`src/test-setup.ts` evaluates this graph for every suite,
 * ahead of any `vi.mock`).
 */
import { ImageOff } from 'lucide-react';
import {
  CopyButton,
  EntityLink,
  ExternalServiceLink,
  FIELD_TYPE_WIDGETS,
  stringifyRaw,
} from '@resource-kit/ui/react';
import { jq, registerWidget } from '@resource-kit/controllers';
import type { FieldType, Widget, WidgetCsvContext, WidgetProps } from '@resource-kit/model';
import { EXTERNAL_URLS } from '@/constants/config';
import i18n from '@/i18n';
import { failHard } from '@resource-kit/core';

declare module '@resource-kit/core' {
  interface WidgetRegistry {
    productTitle: { types: 'text'; modes: 'list' };
    productOptions: { types: 'json'; modes: 'list' };
    productShippingDate: { types: 'text'; modes: 'list' };
  }
}

/**
 * The field type's own widget — level 3 of the resolution order, reached
 * directly because `productOptions` IS the json projection over a derived value.
 */
function typeWidget(type: FieldType): Widget {
  return (
    FIELD_TYPE_WIDGETS.get(type) ??
    failHard(`no default widget is registered for field type "${type}"`)
  );
}

/** The legacy `render` carrier's CSV projection, kept exactly. */
const rawCsv = ({ value }: WidgetCsvContext): string => stringifyRaw(value);

const JsonDisplay = typeWidget('json').display;

/** The two record members these widgets read; see the customers module. */
type ProductRow = {
  shopify_id?: unknown;
  images?: unknown;
  title?: unknown;
  shipping_date_source?: unknown;
};

function rowOf(record: unknown): ProductRow {
  if (typeof record !== 'object' || record === null) return {};
  const row: ProductRow = record;
  return row;
}

const getProductImage = (imagesJson: string | null | undefined) => {
  if (!imagesJson) return null;
  try {
    const images = JSON.parse(imagesJson);
    return images.length > 0 ? images[0] : null;
  } catch {
    return null;
  }
};

function ProductTitle({ value, record, surface }: WidgetProps) {
  if (surface === 'detail') {
    // `value` is `unknown` (the product title, a string) — narrow
    // before rendering; keeps the `value || '-'` falsy fallback.
    return (
      <span className="text-sm font-medium text-foreground">
        {typeof value === 'string' ? value || '-' : '-'}
      </span>
    );
  }
  const title = typeof value === 'string' ? value : '';
  const row = rowOf(record);
  const img = getProductImage(row.images as string | null | undefined);
  const url = typeof img === 'string' ? img : (img && img.src) || '';
  const shopifyId = String(row.shopify_id);
  const shopifyUrl = EXTERNAL_URLS.shopifyAdmin('products', shopifyId);
  return (
    <div className="max-w-sm flex items-center">
      {url ? (
        <img
          src={url}
          alt={row.title as string}
          className="w-8 h-8 object-cover rounded border border-border"
        />
      ) : (
        <div className="w-8 h-8 bg-muted rounded flex items-center justify-center border border-border">
          <ImageOff className="w-3.5 h-3.5 text-muted-foreground" />
        </div>
      )}
      <EntityLink to={`/products/${shopifyId}`} className="font-medium truncate m-2">
        {title}
      </EntityLink>
      {title && <CopyButton value={title} />}
      {shopifyUrl && (
        <ExternalServiceLink href={shopifyUrl} ariaLabel={i18n.t('ui.openInShopify')} />
      )}
    </div>
  );
}

/**
 * The former `fields[].transform`. Kept a plain function so the two projections
 * below cannot drift — that is precisely what the legacy carrier guaranteed by
 * running once, ahead of both the cell and the export.
 */
const deriveOptions = (value: unknown) => ({
  [i18n.t('product.options.primary')]: jq(value, '[0].name'),
  [i18n.t('product.options.values')]: jq(value, '[0].values'),
});

function ProductOptions(props: WidgetProps) {
  return <JsonDisplay {...props} value={deriveOptions(props.value)} />;
}

const productOptionsCsv = ({ value }: WidgetCsvContext): string =>
  stringifyRaw(deriveOptions(value));

function ProductShippingDate({ value, record }: WidgetProps) {
  // Single source of truth: the backend `shipping_date` column (ADR-0018
  // auto-fills it from the synced metafield or the title). list + detail
  // both read the column; `shipping_date_source` shows how it was set.
  const date = value as string | null;
  // `record` is `unknown` here where the legacy renderer held a typed `Product`
  // row. `raw ? String(raw) : null` is the same two steps the old code did in
  // one expression — the truthiness gate on the raw member, then its
  // string interpolation into the label key.
  const raw = rowOf(record).shipping_date_source;
  const source = raw ? String(raw) : null;
  if (!date) return <span className="text-sm text-muted-foreground">-</span>;
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className="text-sm text-foreground">{date}</span>
      {source && (
        <span className="text-[10px] uppercase tracking-wide text-muted-foreground rounded border border-border px-1.5 py-0.5">
          {i18n.t(`product.shippingDateSource.${source}`)}
        </span>
      )}
    </span>
  );
}

/** Called by the feature barrel, which the composition root calls once. */
export function registerProductWidgets(): void {
  // These rows declare no `input`: resolution refills it from the field type
  // default (ADR-0030).
  registerWidget('productTitle', {
    display: ProductTitle,
    csvText: rawCsv,
    supportedTypes: ['text'],
    modes: ['list'],
  });
  registerWidget('productOptions', {
    display: ProductOptions,
    // The RAW value, deliberately: `transform` never touched the form
    // control, only the cell and the export.
    csvText: productOptionsCsv,
    supportedTypes: ['json'],
    modes: ['list'],
  });
  registerWidget('productShippingDate', {
    display: ProductShippingDate,
    csvText: rawCsv,
    // Stored as YYYY-MM-DD — offer date range/eq ops in the QueryBuilder
    // (the display stays a plain text badge). #filterable-coverage.
    filterType: 'date',
    supportedTypes: ['text'],
    modes: ['list'],
  });
}
